<template>
  <el-dialog
    class="dialog-wrapper roles-modal"
    visible
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('defaultLocations.update.updateLocation')
          : $t('defaultLocations.create.createLocation')
      "
    />

    <div class="analysis-data">
      <h2>
        {{ `${analysis.code} - ${analysis.description}` }}
      </h2>
    </div>

    <ValidationObserver
      ref="defaultLocationsForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="10">
            <FormItem
              rules="required"
              :label="$t('defaultLocations.create.location')"
            >
              <el-autocomplete
                v-model="locationName"
                style="width: 100%"
                :fetch-suggestions="locationsList"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @select="
                  (item) => {
                    formData.locationId = item.value;
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
          <el-col :span="10">
            <FormItem>
              <el-checkbox v-model="formData.legalConstraint">
                {{ $t('defaultLocations.create.legalConstraint') }}
              </el-checkbox>
            </FormItem>
          </el-col>
        </el-row>

        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="10">
            <FormItem :label="$t('defaultLocations.create.motivation')">
              <el-input v-model="formData.motivation" />
            </FormItem>
          </el-col>
        </el-row>
        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('defaultLocations.create.abortAction') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createUpdateLocation)"
          >
            {{
              isModalEdit
                ? $t('defaultLocations.update.updateLocation')
                : $t('defaultLocations.create.createLocation')
            }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { locationsManagementApi, analysesApi } from '@/api';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormItem from '@/components/FormItem.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';
  import FormButtons from '@/components/FormButtons.vue';

  const defaultData = {
    legalConstraint: false,
    motivation: '',
    locationId: '',
    analysisId: '',
  };

  export default {
    name: 'CreateEditDefaultLocation',
    components: {
      ContentHeader,
      FormItem,
      AutocompleteItem,
      FormButtons,
    },
    props: {
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
      analysis: { type: Object, default: () => {} },
    },
    data() {
      return {
        formData: { ...defaultData },
        locationName: null,
      };
    },
    async created() {
      try {
        if (this.isModalEdit) {
          const { analysisId, legalConstraint, locationId, motivation } =
            this.initialData;
          this.formData = {
            ...defaultData,
            analysisId,
            legalConstraint,
            locationId,
            motivation,
          };
          if (locationId) {
            this.locationName = this.initialData.location.name;
          }
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
    methods: {
      async createUpdateLocation() {
        this.formData.analysisId = this.analysis.id;
        try {
          if (this.isModalEdit) {
            await analysesApi.defaultLocationsPatch(
              this.initialData.id,
              this.formData
            );
          } else {
            await analysesApi.defaultLocationsCreate(this.formData);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('defaultLocations.update.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.defaultLocationsForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async locationsList(value, cb) {
        if (value && value.length >= 3) {
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            value,
            false
          );
          const locations = response.data.map((c) => ({
            label: c.name,
            value: c.id,
          }));
          cb(locations);
        } else {
          cb([]);
        }
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.$refs.defaultLocationsForm.reset();
        this.$emit('dialogClose', { success: false });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .analysis-data {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    font-size: 16px;
    color: $--color-white;
  }
</style>
